import { Component } from '@theme/component';

/** Floating desktop disclosures and a native, focus-contained mobile dialog. */
class HenryPillMenu extends Component {
  #controller;
  #resizeObserver;
  #closeTimer;
  #dialog;
  #history = [];
  #scrollStyle;
  #desktop = matchMedia('(min-width: 1360px)');

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#setup();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #setup() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#dialog = this.querySelector('.pd-mobile-dialog');
    this.addEventListener('click', this.#click, { signal, capture: true });
    this.addEventListener('keydown', this.#keydown, { signal });
    this.#dialog?.addEventListener('close', this.#afterClose, { signal });
    this.#dialog?.addEventListener('cancel', (event) => {
      if (!this.#history.length) return;
      event.preventDefault();
      this.#back();
    }, { signal });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.#closeDesktop();
    }, { signal });
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.#closeDesktop();
    }, { signal });
    for (const details of this.querySelectorAll('.pd-root-menu, .pd-country-menu')) {
      details.addEventListener('toggle', () => {
        if (details.open) {
          this.#prepareDesktop(details);
          this.#closeDesktop(details);
        }
        else delete details.dataset.hoverOpened;
      }, { signal });
      if (!details.matches('.pd-root-menu')) continue;
      const trigger = details.closest('.pd-root-item') || details;
      trigger.addEventListener('focusin', () => this.#prepareDesktop(details), { signal });
      trigger.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || !this.#desktop.matches) return;
        this.#prepareDesktop(details);
        clearTimeout(this.#closeTimer);
        this.#closeDesktop(details);
        if (!details.open) details.dataset.hoverOpened = 'true';
        details.open = true;
      }, { signal });
      trigger.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.#closeTimer = setTimeout(() => {
          if (!trigger.contains(document.activeElement)) details.open = false;
        }, 220);
      }, { signal });
    }
    this.#desktop.addEventListener('change', () => {
      this.#closeDesktop();
      this.#dialog?.close();
      this.#measure();
    }, { signal });
    this.#resizeObserver = new ResizeObserver(this.#measure);
    this.#resizeObserver.observe(this);
    const group = this.closest('#header-group');
    if (group) this.#resizeObserver.observe(group);
    this.#measure();
  }

  #measure = () => {
    const group = this.closest('#header-group');
    if (!group) return;
    // Read together before writing inherited CSS variables; alternating these
    // reads and writes can recalculate the entire header more than once.
    const headerHeight = `${this.offsetHeight}px`;
    const groupHeight = `${group.offsetHeight}px`;
    // Round up the rendered height so the header cannot cover a sticky pill's top border.
    const stickyHeaderHeight = `${Math.ceil(group.getBoundingClientRect().height)}px`;
    for (const [style, name, value] of [
      [document.body.style, '--header-height', headerHeight],
      [document.body.style, '--header-group-height', groupHeight],
      [document.documentElement.style, '--pd-sticky-header-height', stickyHeaderHeight],
    ]) {
      if (style.getPropertyValue(name) !== value) style.setProperty(name, value);
    }
  };

  #click = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const summary = target.closest('.pd-root-menu > summary');
    if (summary?.parentElement.dataset.hoverOpened && event.detail > 0) {
      // A mouse click must not immediately undo the preceding hover opening.
      event.preventDefault();
      delete summary.parentElement.dataset.hoverOpened;
      return;
    }
    if (target.closest('[data-mobile-toggle]')) {
      this.#open();
    } else if (target.closest('[data-mobile-close]') || target === this.#dialog) {
      this.#dialog?.close();
    } else if (target.closest('[data-menu-back]')) {
      this.#back();
    } else if (target.closest('[data-menu-open]')) {
      const button = target.closest('[data-menu-open]');
      const current = this.querySelector('.pd-mobile-page:not([hidden])');
      this.#history.push({ key: current?.dataset.menuPage || 'root', button, scroll: this.querySelector('.pd-mobile-body').scrollTop });
      this.#showPage(button.dataset.menuOpen);
    } else if (this.#dialog?.open && target.closest('a, [data-open-tool], search-button')) {
      this.#dialog.close();
    }
  };

  #keydown = (event) => {
    const rootItem = event.target.closest?.('.pd-root-item');
    const details = rootItem?.querySelector('.pd-root-menu') || event.target.closest?.('.pd-root-menu, .pd-country-menu');
    if (event.key === 'Escape' && details?.open) {
      event.preventDefault();
      details.open = false;
      details.querySelector('summary')?.focus();
    }
    if (event.key === 'ArrowDown' && event.target.matches('summary, .pd-root-item > .pd-root-link') && details) {
      event.preventDefault();
      this.#prepareDesktop(details);
      this.#closeDesktop(details);
      details.open = true;
      details.querySelector('a, select')?.focus();
    }
  };

  #prepareDesktop(details) {
    // Keep closed menus inert, including on phones; no request is needed to open.
    const template = details.querySelector('template[data-desktop-menu]');
    if (template instanceof HTMLTemplateElement) template.replaceWith(template.content);
  }

  #closeDesktop(except) {
    for (const details of this.querySelectorAll('.pd-root-menu[open], .pd-country-menu[open]')) {
      if (details !== except) details.open = false;
    }
  }

  #open() {
    if (!this.#dialog || this.#dialog.open) return;
    this.#history = [];
    this.#showPage('root', false);
    const top = Math.max(12, this.querySelector('.pd-pill-bar').getBoundingClientRect().top);
    this.#dialog.style.setProperty('--pd-dialog-top', `${top}px`);
    this.#scrollStyle = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.querySelector('[data-mobile-toggle]')?.setAttribute('aria-expanded', 'true');
    this.#dialog.showModal();
  }

  #showPage(key, focus = true) {
    // The markup is already in the response; opening a category needs no fetch.
    // Instantiate only that category, retaining its nodes for Back and reopen.
    const template = this.querySelector(`template[data-mobile-menu-group="${CSS.escape(key)}"]`);
    if (template instanceof HTMLTemplateElement) template.replaceWith(template.content);
    const pages = [...this.querySelectorAll('[data-menu-page]')];
    const next = pages.find((page) => page.dataset.menuPage === key);
    if (!next) return;
    for (const page of pages) page.hidden = page !== next;
    this.querySelector('.pd-mobile-body').scrollTop = 0;
    if (focus) next.querySelector('h2, button, a')?.focus({ preventScroll: true });
  }

  #back() {
    const previous = this.#history.pop();
    if (!previous) return;
    this.#showPage(previous.key, false);
    this.querySelector('.pd-mobile-body').scrollTop = previous.scroll;
    previous.button?.focus({ preventScroll: true });
  }

  #afterClose = () => {
    this.querySelector('[data-mobile-toggle]')?.setAttribute('aria-expanded', 'false');
    if (this.#scrollStyle !== undefined) document.documentElement.style.overflow = this.#scrollStyle;
    this.#scrollStyle = undefined;
  };

  #cleanup() {
    this.#dialog?.close();
    this.#afterClose();
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    document.documentElement.style.removeProperty('--pd-sticky-header-height');
    clearTimeout(this.#closeTimer);
  }
}

if (!customElements.get('henry-pill-menu')) customElements.define('henry-pill-menu', HenryPillMenu);
